import { useEffect } from 'react';

import { ALERT_DISPOSITION_LABELS, VENDOR_LABELS } from '@/types/domain';

import type { AlertDetail, AlertRow, SupportingTransaction } from './api';

interface AlertDetailDrawerProps {
  row: AlertRow;
  detail: AlertDetail | null;
  /**
   * Supporting transactions from the dedicated
   * `/api/v1/alerts/:id/transactions` endpoint. `null` until the query
   * resolves; the empty-array case renders a "no txns indexed yet"
   * note (adapter-derived alerts don't carry supporting txns today).
   */
  supportingTransactions: SupportingTransaction[] | null;
  loading: boolean;
  error: unknown;
  onClose: () => void;
}

/**
 * Right-side drawer showing the vendor-verbatim payload, grouping-key
 * breakdown, and supporting transactions for one `HistoricalAlert`.
 *
 * This is its own file so `Alerts.tsx` stays under the 500-line cap. The
 * drawer is rendered by `Alerts.tsx` when a row is clicked; it owns the
 * escape-to-close key handler so the parent screen doesn't need to.
 */
export default function AlertDetailDrawer({
  row,
  detail,
  supportingTransactions,
  loading,
  error,
  onClose,
}: AlertDetailDrawerProps) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const txns = supportingTransactions ?? detail?.supportingTransactions ?? null;

  return (
    <aside
      className="alert-detail-drawer"
      role="complementary"
      aria-label={`Alert ${row.displayId}`}
      data-testid="alert-detail-drawer"
    >
      <header className="alert-detail-head">
        <div>
          <div className="eyebrow">§ ALERT</div>
          <h2 className="serif alert-detail-title">{row.displayId}</h2>
          <p className="alert-detail-sub">
            {row.scenarioName} · {VENDOR_LABELS[row.sourcePlatform]}
          </p>
        </div>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={onClose}
          data-testid="alert-detail-close"
        >
          Close
        </button>
      </header>

      <section className="alert-detail-section">
        <div className="eyebrow">HEADLINE</div>
        <p className="alert-detail-headline">{row.headline}</p>
      </section>

      <section className="alert-detail-section">
        <div className="eyebrow">DISPOSITION</div>
        <p>{ALERT_DISPOSITION_LABELS[row.disposition]}</p>
      </section>

      <section className="alert-detail-section">
        <div className="eyebrow">GROUPING KEY</div>
        <p className="mono alert-detail-grouping-key">{row.groupingKey}</p>
        {loading && <p className="alert-detail-status">Loading breakdown…</p>}
        {error ? (
          <p className="alert-detail-error" role="alert">
            Couldn&apos;t load alert detail.
          </p>
        ) : null}
        {detail && (
          <dl className="alert-detail-grouping">
            {detail.groupingBreakdown.map((seg) => (
              <div key={seg.label} className="alert-detail-grouping-row">
                <dt className="mono">{seg.label}</dt>
                <dd>{seg.value}</dd>
              </div>
            ))}
          </dl>
        )}
      </section>

      <section className="alert-detail-section">
        <div className="eyebrow">SUPPORTING TRANSACTIONS</div>
        {txns === null ? (
          <p className="alert-detail-status">Loading transactions…</p>
        ) : txns.length === 0 ? (
          <p className="alert-detail-status">
            No supporting transactions indexed for this alert yet — the vendor payload above is
            the source of truth.
          </p>
        ) : (
          <table
            className="sentinel-table alert-detail-txns"
            aria-label="Supporting transactions"
            data-testid="alert-detail-txns"
          >
            <thead>
              <tr>
                <th>Tx</th>
                <th>Msg</th>
                <th>Direction</th>
                <th>Amount</th>
                <th>Counterparty</th>
              </tr>
            </thead>
            <tbody>
              {txns.map((tx) => (
                <tr key={tx.txId}>
                  <td className="mono">{tx.txId}</td>
                  <td className="mono">{tx.messageType}</td>
                  <td>{tx.direction}</td>
                  <td className="mono">
                    {tx.amount} {tx.currency}
                  </td>
                  <td>{tx.counterparty}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {detail && (
        <section className="alert-detail-section">
          <div className="eyebrow">VENDOR PAYLOAD (VERBATIM)</div>
          <pre
            className="alert-detail-raw mono"
            data-testid="alert-detail-raw-payload"
          >
            {detail.rawPayload}
          </pre>
        </section>
      )}
    </aside>
  );
}
